import { z } from 'zod';
import { escapeHtml, safeUrl } from '../../render/escape.js';
import type { DefModuleMountT } from '../../schema/split.js';
import { ImageValue, Slug } from '../../schema/types.js';
import { ModuleOpError, identityMigration, type FirstPartyModule, type ModuleOperation } from '../kernel.js';
import {
  CommerceCatalogConfig,
  CommerceCatalogData,
  GiftCardOptions,
  Money,
  Product,
  ProductKind,
  type CommerceCatalogConfigT,
  type CommerceCatalogDataT,
  type MoneyT,
} from './schema.js';

// Operation-input ids reuse the canonical Slug (LOW-6): its length bounds
// travel with the regex, so an operation can't accept an over-long id the
// data schema would later reject.
const id = Slug;
const productName = z.string().min(1).max(160);
const productDescription = z.string().max(600);
const productSku = z.string().max(64);

const addProductInput = Product;
const updateProductInput = z.object({
  id,
  // For the optional fields a null EXPLICITLY CLEARS the field (a merchant
  // removing a product photo/sku/description/gift-card options); an absent key
  // leaves it unchanged. name/price/available/kind are never clearable.
  fields: z.object({
    name: productName.optional(),
    description: productDescription.nullable().optional(),
    price: Money.optional(),
    image: ImageValue.nullable().optional(),
    available: z.boolean().optional(),
    sku: productSku.nullable().optional(),
    kind: ProductKind.optional(),
    giftCard: GiftCardOptions.nullable().optional(),
  }).strict().refine((fields) => Object.keys(fields).length > 0, 'at least one field is required'),
}).strict();
const removeProductInput = z.object({ id }).strict();
const reorderProductsInput = z.object({ orderedIds: z.array(id) }).strict();
const setAvailableInput = z.object({ id, available: z.boolean() }).strict();

type CatalogProduct = CommerceCatalogDataT['products'][number];
type Locale = CommerceCatalogConfigT['locale'];

const DEFAULT_CONFIG: CommerceCatalogConfigT = { view: 'grid', locale: 'cs' };

function invalidInput(error: z.ZodError): ModuleOpError {
  return new ModuleOpError('invalid-input', error.issues[0]?.message ?? 'invalid operation input');
}

function parseInput<T>(schema: z.ZodType<T>, input: unknown): T {
  const parsed = schema.safeParse(input);
  if (!parsed.success) throw invalidInput(parsed.error);
  return parsed.data;
}

function cloneData(data: CommerceCatalogDataT): CommerceCatalogDataT {
  return structuredClone(data);
}

function findProduct(data: CommerceCatalogDataT, productId: string): CatalogProduct {
  const product = data.products.find((candidate) => candidate.id === productId);
  if (!product) throw new ModuleOpError('product-not-found', `product "${productId}" was not found`);
  return product;
}

/** A product's price currency must always match the catalog's currency —
 *  checked on every write that sets a price, so a mismatched-currency product
 *  can never enter stored data through the operations. (The data schema also
 *  refines this, catching a hand-seeded snapshot; this gives an earlier,
 *  clearer ModuleOpError.) */
function assertCurrencyMatches(data: CommerceCatalogDataT, price: MoneyT): void {
  if (price.currency !== data.currency) {
    throw new ModuleOpError('currency-mismatch', `price currency "${price.currency}" does not match catalog currency "${data.currency}"`);
  }
}

function reorderByIds<T extends { id: string }>(items: T[], orderedIds: string[], label: string): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const uniqueIds = new Set(orderedIds);
  const hasEveryItem = items.every((item) => uniqueIds.has(item.id));
  if (orderedIds.length !== items.length || uniqueIds.size !== orderedIds.length || !hasEveryItem) {
    throw new ModuleOpError('invalid-order', `${label} order must contain every current id exactly once`);
  }
  return orderedIds.map((orderedId) => byId.get(orderedId)!);
}

function operation<T>(
  schema: z.ZodType<T>,
  describe: string,
  transform: (data: CommerceCatalogDataT, input: T) => void,
): ModuleOperation<CommerceCatalogDataT> {
  return {
    input: schema,
    describe,
    apply(data, input) {
      const parsed = parseInput(schema, input);
      const result = cloneData(data);
      transform(result, parsed);
      return result;
    },
  };
}

const operations: Record<string, ModuleOperation<CommerceCatalogDataT>> = {
  addProduct: operation(addProductInput, 'Add a product to the catalog.', (data, input) => {
    if (data.products.some((product) => product.id === input.id)) {
      throw new ModuleOpError('duplicate-id', `product "${input.id}" already exists`);
    }
    assertCurrencyMatches(data, input.price);
    data.products.push(input);
  }),
  updateProduct: operation(updateProductInput, 'Update fields on a catalog product.', (data, input) => {
    const product = findProduct(data, input.id);
    if (input.fields.price) assertCurrencyMatches(data, input.fields.price);
    const target = product as Record<string, unknown>;
    // A null value CLEARS an optional field; a non-null value replaces it; an
    // absent key (zod dropped it) leaves it unchanged.
    for (const [key, value] of Object.entries(input.fields)) {
      if (value === null) delete target[key];
      else target[key] = value;
    }
    // Re-validate the MERGED product so a patch can never leave it in a state
    // the schema forbids (e.g. kind flipped to 'gift_card' with no giftCard,
    // or a 'gift_card' whose giftCard was just cleared).
    const revalidated = Product.safeParse(product);
    if (!revalidated.success) throw invalidInput(revalidated.error);
  }),
  removeProduct: operation(removeProductInput, 'Remove a product from the catalog.', (data, input) => {
    findProduct(data, input.id);
    data.products = data.products.filter((product) => product.id !== input.id);
  }),
  reorderProducts: operation(reorderProductsInput, 'Reorder all products in the catalog.', (data, input) => {
    data.products = reorderByIds(data.products, input.orderedIds, 'product');
  }),
  setAvailable: operation(setAvailableInput, 'Set whether a product is available for purchase.', (data, input) => {
    findProduct(data, input.id).available = input.available;
  }),
};

/**
 * Format a Money value for display. Pure; uses the runtime's built-in
 * Intl.NumberFormat (never a currency-formatting library), including for the
 * minor-unit exponent — which is NOT always 2 (JPY/KRW use 0, BHD uses 3) —
 * so amountMinor is converted to major units via the currency's own resolved
 * fraction digits rather than an assumed "divide by 100". `locale` (a site's
 * cs/en/pl) drives grouping and symbol placement (CZK -> "1 299,00 Kč" under
 * cs, "CZK 1,299.00" under en). This is a DISPLAY edge only — K2/K3 totaling
 * stays in integer minor units and never round-trips through this string.
 */
export function formatMoney(money: MoneyT, locale: string = 'en'): string {
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: money.currency });
    // resolvedOptions() types this as optional across ALL NumberFormat styles;
    // for style:'currency' it is always populated at runtime — the fallback
    // is a type-checker satisfier, not a reachable branch.
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(money.amountMinor / 10 ** digits);
  } catch {
    // The schema only enforces the ISO-4217 SHAPE (3 uppercase letters), not
    // that it is a real registered code — never throw from a render path.
    return `${(money.amountMinor / 100).toFixed(2)} ${money.currency}`;
  }
}

function sectionOpen(mount: DefModuleMountT, view: string): string {
  return `<section id="${escapeHtml(mount.anchor)}" class="ve-module ve-commerce-catalog ve-commerce-catalog--${escapeHtml(view)}">`;
}

function renderProductImage(image: CatalogProduct['image']): string {
  if (!image) return '';
  return `      <img class="ve-commerce-catalog__card-image" src="${safeUrl(image.src)}" alt="${escapeHtml(image.alt)}" loading="lazy">`;
}

/**
 * Render one product card. The buy action is INERT markup: checkout wiring
 * lands with K2/K3, so this is a plain button carrying the product id, not a
 * form action.
 */
function renderProductCard(product: CatalogProduct, locale: Locale): string {
  const lines = ['    <div class="ve-commerce-catalog__card">'];
  const image = renderProductImage(product.image);
  if (image) lines.push(image);
  lines.push(`      <h3 class="ve-commerce-catalog__card-name">${escapeHtml(product.name)}</h3>`);
  if (product.description) lines.push(`      <p class="ve-commerce-catalog__card-desc">${escapeHtml(product.description)}</p>`);
  lines.push(
    `      <span class="ve-commerce-catalog__card-price">${escapeHtml(formatMoney(product.price, locale))}</span>`,
    `      <button type="button" class="ve-commerce-catalog__card-cta" data-veldon-buy="${escapeHtml(product.id)}">Buy</button>`,
    '    </div>',
  );
  return lines.join('\n');
}

function renderProductGrid(
  mount: DefModuleMountT,
  data: CommerceCatalogDataT,
  config: CommerceCatalogConfigT = DEFAULT_CONFIG,
): { html: string } {
  const parsedConfig = CommerceCatalogConfig.parse(config);
  const cards = data.products
    .filter((product) => product.available)
    .map((product) => renderProductCard(product, parsedConfig.locale));
  const columnsClass = parsedConfig.columns ? ` ve-commerce-catalog__grid--cols-${parsedConfig.columns}` : '';
  const gridClass = `ve-commerce-catalog__grid ve-commerce-catalog__grid--${parsedConfig.view}${columnsClass}`;
  const parts = [sectionOpen(mount, 'product-grid'), `  <div class="${gridClass}">`, ...cards, '  </div>', '</section>'];
  return { html: parts.join('\n') };
}

/** Optional single-product view: config.productId selects which product to
 *  show. An unset or non-matching productId renders an empty section rather
 *  than throwing — config shape validity and data content are separate
 *  concerns, and a missing product is not a resolve failure. */
function renderProductDetail(
  mount: DefModuleMountT,
  data: CommerceCatalogDataT,
  config: CommerceCatalogConfigT = DEFAULT_CONFIG,
): { html: string } {
  const parsedConfig = CommerceCatalogConfig.parse(config);
  const product = parsedConfig.productId ? data.products.find((candidate) => candidate.id === parsedConfig.productId) : undefined;
  const parts = [sectionOpen(mount, 'product-detail')];
  if (product) parts.push(renderProductCard(product, parsedConfig.locale));
  parts.push('</section>');
  return { html: parts.join('\n') };
}

const migrate = identityMigration('commerce.catalog', CommerceCatalogData);

// Server-side-only per-instance config (SiteModulesRepository / D7 Unit B): a
// payment-provider REFERENCE (e.g. a Stripe Connect account id — a ref, never
// a secret key; the real key is resolved server-side at checkout, K2/K3) plus
// tax display policy. Never part of the ContentSnapshot, never readable by
// editing surfaces or agents.
const privateConfig = z.object({
  paymentProviderRef: z.string().max(200).optional(),
  taxIncluded: z.boolean().default(true),
  taxRatePercent: z.number().min(0).max(100).optional(),
}).strict();

export const commerceCatalog: FirstPartyModule<CommerceCatalogDataT, CommerceCatalogConfigT> = {
  id: 'commerce.catalog',
  contractVersion: 1,
  dataVersion: 1,
  dataSchema: CommerceCatalogData,
  configSchema: CommerceCatalogConfig,
  privateConfigSchema: privateConfig,
  operations,
  views: {
    'product-grid': { render: renderProductGrid },
    'product-detail': { render: renderProductDetail },
  },
  migrate,
};
